/**
 * Patient intake forms, as patient-kapi defines and stores them.
 *
 * Mirrors `co.hiwally.patient.api.domain.forms.definition` one to one. Labels and option titles
 * are load-bearing downstream (answers are matched to Ascend by the exact strings), so nothing
 * here renames or reshapes what the server sends.
 */

/** Whatever the server defines; the app never branches on it. */
export type FormType = string

export type StepType = 'INFO' | 'QUESTION' | 'CHOICE' | 'DATE' | 'ADDRESS' | 'CONSENT'

export type OptionType = 'SELECTABLE' | 'EDITABLE' | 'EXPANDABLE_SELECT'

export interface SubOption {
	id: number
	title: string
}

export interface ChoiceOption {
	id: number
	title: string
	type: OptionType
	/** EXPANDABLE_SELECT only. Ids share a namespace with the options they sit under. */
	subOptions?: SubOption[]
}

export interface ShowIf {
	/** Shown when the age derived from a DATE step's answer is under `value` years. */
	type: 'AGE_UNDER'
	stepId: number
	value: number
}

export interface FormStep {
	id: number
	type: StepType
	/** The question as it is stored with the answers, e.g. "Shipping address". */
	label: string
	showIf?: ShowIf
	title: string
	description?: string
	/** INFO: the one button. */
	cta?: string
	primaryButtonLabel?: string
	canSkip?: boolean
	/** QUESTION: placeholder for the text field. */
	inputLabel?: string
	/** CHOICE. */
	isMultiSelect?: boolean
	options?: ChoiceOption[]
	/** CONSENT: markdown limited to paragraphs and `**bold**`; `preview` is the collapsed text. */
	preview?: string
	text?: string
	isExpandable?: boolean
}

export interface FormDefinition {
	formType: FormType
	name: string
	/** Raised whenever the form changes; handed back on submit so a consent can be traced to its wording. */
	version: number
	steps: FormStep[]
}

/** A form waiting on the signed-in patient. */
export interface PendingForm {
	id: number
	/** Display-ready, e.g. "Welcome Form". */
	name: string
	formType: FormType
	created: string
	/** Whatever the provisioner attached; `schedulingLinkId` when the form gates a booking. */
	config: { schedulingLinkId?: string } & Record<string, unknown>
	/** What the form asks, so opening it needs no further call. */
	definition: FormDefinition
}

export interface AddressAnswer {
	street1: string
	street2: string
	city: string
	state: string
	zip: string
}

/**
 * One question and its answers, as submitted.
 *
 * Every answer is a string except a shipping address, which the server expects as an object
 * and stringifies itself.
 */
export interface FormEntry {
	questionId: number
	question: string
	answers: (string | AddressAnswer)[]
}

/** A form the patient submitted, as a row in their history. */
export interface CompletedForm {
	id: number
	name: string
	formType: FormType
	submitted: string
	/** Absent on submissions from before versions were recorded. */
	definitionVersion: number | null
}

/**
 * One question and what was answered, as the server stored it.
 *
 * Answers are strings by the time they come back; an address was flattened on the way in.
 * Older submissions have been seen with nulls in either field, so nothing here is assumed.
 */
export interface SubmittedEntry {
	questionId?: number | null
	question?: string | null
	answers?: unknown[] | null
}

export interface CompletedFormDetail extends CompletedForm {
	entries: SubmittedEntry[]
}

/** The slice of a Micronaut `Page` the app reads. */
export interface PageResponse<T> {
	content: T[]
	totalSize: number
	totalPages: number
	pageNumber: number
}

/** The server's code when a form has already been submitted, by this device or another. */
export const FORM_ALREADY_SUBMITTED = 'FORM_ALREADY_SUBMITTED'

export interface SubmitPendingFormRequest {
	entries: FormEntry[]
	/** The version of the definition the answers were given against. */
	definitionVersion?: number
}
